import type { ReactNode } from "react";
import ActionButton from "@/components/ui/action-button";
import type { Brandkit } from "../types/api.types";

interface BoldLayoutProps {
	showBranding: boolean;
	brandName: string;
	brandLogo?: string | null;
	brandkit: Brandkit;
	title: string;
	description?: string;
	/** Small uppercase text above the title (e.g., "QUESTION 1 OF 2") */
	eyebrow?: string;
	showBackButton?: boolean;
	onBack?: () => void;
	onContinue?: () => void;
	continueLabel?: string;
	continueDisabled?: boolean;
	/** Use type="submit" for form buttons */
	isFormSubmit?: boolean;
	children: ReactNode;
	stepKey?: string;
	/** Center the content vertically (for steps without much content) */
	centered?: boolean;
	/** Progress indicator (e.g., { current: 2, total: 5 }) */
	progress?: { current: number; total: number };
	showFooter?: boolean;
	/** Enable dark mode */
	isDarkMode?: boolean;
}

export default function BoldLayout({
	showBranding,
	brandName,
	brandLogo,
	brandkit,
	eyebrow,
	title,
	description,
	children,
	progress,
	showFooter = true,
	continueLabel = "Continue",
	showBackButton = false,
	centered = true,
	isDarkMode = false,
	onBack,
	onContinue,
	continueDisabled = false,
	isFormSubmit = false,
	stepKey,
}: BoldLayoutProps) {
	return (
		<>
			<div
				key={stepKey}
				className={`min-h-screen flex flex-col overflow-hidden pb-[calc(91px+64px)] animate-fade-in-up ${isDarkMode ? "bg-gray-950" : "bg-gray-50"}`}
			>
				{/* Progress bar or accent bar at top */}
				{progress ? (
					<div
						className={`fixed top-0 w-full h-1 shrink-0 ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`}
					>
						<div
							className="h-full transition-all duration-500 ease-out"
							style={{
								width: `${(progress.current / progress.total) * 100}%`,
								backgroundColor: brandkit.color_primary_hex,
							}}
						/>
					</div>
				) : (
					<div
						className="fixed top-0 h-1 w-full shrink-0"
						style={{
							backgroundColor: brandkit.color_primary_hex,
						}}
					/>
				)}

				{/* Main container - centered with max width */}
				<div
					className={`flex-1 flex flex-col w-full max-w-xl mx-auto px-6 md:px-8 ${centered ? "justify-center" : ""}`}
				>
					{/* Header with logo */}
					<header
						className={`pt-16 pb-10 shrink-0 ${centered ? "" : "pt-20"}`}
					>
						{brandLogo ? (
							<img
								src={brandLogo}
								alt={brandName}
								className="h-8 md:h-10 w-auto object-contain"
							/>
						) : (
							<h1
								className={`text-xl font-bold`}
								style={{
									fontFamily: "var(--font-body, 700)",
									fontWeight: "var(--font-body-weight, 600)",
									color: isDarkMode ? "#ffffff" : "#1f1f1f",
								}}
							>
								{brandName}
							</h1>
						)}
					</header>

					{/* Content area */}
					<main className="flex-1 flex flex-col">
						{/* Eyebrow */}
						{eyebrow && (
							<p
								className={`text-xs font-medium uppercase tracking-widest mb-3 ${isDarkMode ? "text-gray-400" : "text-tertiary"}`}
								style={{
									fontFamily: "var(--font-body, inherit)",
									fontWeight: "var(--font-body-weight, 400)",
								}}
							>
								{eyebrow}
							</p>
						)}

						{/* Title */}
						<h1
							className={`text-[36px] leading-[1.3] tracking-tight ${isDarkMode ? "text-white" : "text-t-primary"}`}
							style={{
								fontFamily: "var(--font-heading, inherit)",
								fontWeight: "var(--font-heading-weight, 600)",
							}}
						>
							{title}
						</h1>

						{/* Subtitle */}
						{description ? (
							<p
								className={`mt-3 text-base md:text-lg leading-relaxed ${isDarkMode ? "text-gray-300" : "text-secondary"}`}
								style={{
									fontFamily: "var(--font-body, inherit)",
									fontWeight: "var(--font-body-weight, 400)",
								}}
							>
								{description}
							</p>
						) : null}

						{/* Main content */}
						<div className="mt-8 md:mt-10">{children}</div>
					</main>
				</div>
			</div>

			{/* Footer with buttons - fixed at bottom, outside animated wrapper */}
			{showFooter && (
				<footer className="fixed bottom-0 z-10 shrink-0 w-full">
					{/* Fade gradient above toolbar */}
					<div
						className={`h-16 pointer-events-none ${
							isDarkMode
								? "bg-linear-to-t from-gray-950 to-transparent"
								: "bg-linear-to-t from-gray-50 to-transparent"
						}`}
					/>
					<div
						className={`py-5 md:py-6 ${isDarkMode ? "bg-gray-900 border-t border-gray-800" : "bg-white border-t border-gray-100"}`}
					>
						<div className="w-full max-w-xl mx-auto px-6 md:px-8">
							{/* Full-width button when no back button and no progress */}
							{!showBackButton && !progress ? (
								<div className="space-y-4">
									<ActionButton
										type={
											isFormSubmit ? "submit" : "button"
										}
										onClick={
											isFormSubmit
												? undefined
												: onContinue
										}
										disabled={continueDisabled}
										className="w-full"
									>
										{continueLabel}
										<svg
											className="w-5 h-5"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth={2}
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M9 5l7 7-7 7"
											/>
										</svg>
									</ActionButton>
									{showBranding && (
										<div className="flex justify-center">
											{/* <span
												className={`text-xs sm:text-sm font-sans ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
											>
												Powered by{" "}
												<span className="block sm:inline"></span>{" "}
												<a
													href="https://sayabout.us/"
													target="_blank"
													rel="noopener noreferrer"
													className="underline hover:opacity-80"
												>
													Say About Us
												</a>
											</span> */}
											<a
												href="https://sayabout.us/"
												target="_blank"
												rel="noopener noreferrer"
												className="underline block hover:opacity-80"
											>
												<img
													src="/images/badge-sayaboutus-2.svg"
													alt="build social proof with Say About Us"
													className="h-7 w-auto"
												/>
											</a>
										</div>
									)}
								</div>
							) : (
								<div className="flex items-center gap-3">
									<div className="w-30 shrink-0">
										{showBackButton && onBack ? (
											<ActionButton
												variant="secondary"
												isDarkMode={isDarkMode}
												onClick={onBack}
												className="w-full"
											>
												<svg
													className="w-4 h-4"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
													strokeWidth={2}
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M15 19l-7-7 7-7"
													/>
												</svg>
												Back
											</ActionButton>
										) : (
											<div className="w-full" />
										)}
									</div>

									<div className="flex-1 flex justify-center">
										{showBranding && (
											// <span
											// 	className={`text-xs sm:text-sm font-sans ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
											// >
											// 	Powered by{" "}
											// 	<span className="block sm:inline"></span>{" "}
											// 	<a
											// 		href="https://sayabout.us/"
											// 		target="_blank"
											// 		rel="noopener noreferrer"
											// 		className="underline hover:opacity-80"
											// 	>
											// 		Say About Us
											// 	</a>
											// </span>
											<a
												href="https://sayabout.us/"
												target="_blank"
												rel="noopener noreferrer"
												className="underline block hover:opacity-80"
											>
												<img
													src="/images/badge-sayaboutus-2.svg"
													alt="build social proof with Say About Us"
													className="h-7 w-auto"
												/>
											</a>
										)}
									</div>

									<div className="w-30 shrink-0">
										{(onContinue || isFormSubmit) && (
											<ActionButton
												type={
													isFormSubmit
														? "submit"
														: "button"
												}
												onClick={
													isFormSubmit
														? undefined
														: onContinue
												}
												disabled={continueDisabled}
												className="w-full"
											>
												{continueLabel}
												<svg
													className="w-4 h-4"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
													strokeWidth={2}
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M9 5l7 7-7 7"
													/>
												</svg>
											</ActionButton>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				</footer>
			)}
		</>
	);
}
